import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
//local
import {Tag} from "components/Tag";
import {LockValue} from "components/LockValue";
import {CustomTooltip} from "components/CustomTooltip";
import {ButtonBookmarkByMembers} from "components/buttons/ButtonBookmarkByMembers";
import {TimeLeft} from "components/TimeLeft";
import {IconRefresh, IconDotFilled} from "components/icons";
import {dateFormatUTC, dateTimeFormatUTC, dateEndDay} from "helpers/datetime";
import {toExternalRftId, toPublicRftId, toRequestToUsId} from "constants/routes.const";
import {color} from "constants/color.consts";
import cn from "classnames";

const columnClass = "requests-card_body__column __wrapper";

const PublicRfpsTableItem = ({
  publicRfp,
  currentUser,
  language,
  organizationTurnover,
  isExternal,
  showInfoAndClick,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const availableIn =
    publicRfp.ProjectItem?.Project?.availableIn ?? publicRfp.availableIn;
  const readedRequest = useMemo(() => !!publicRfp.RequestReadedByMember, [publicRfp]);
  const countAvailableIn = availableIn?.length > 1 ? ` +${availableIn?.length - 1}` : "";
  const categories = [
    ...(publicRfp.consultingServices ?? []),
    ...(publicRfp.entrepreneurOffers ?? []),
    ...(publicRfp.entrepreneurWithin ?? []),
    ...(publicRfp.materialSupplierType ?? []),
  ];
  const countCategories = categories?.length > 1 ? ` +${categories?.length - 1}` : "";
  const isHideRfpInfo =
    organizationTurnover && organizationTurnover < publicRfp.minRevenue;

  const iconDotFilled = <IconDotFilled color={color.red} />;

  const TooltipTag = ({data, count}) => (
    <CustomTooltip text={data.map((item) => t(item)).join(", ")} disabled={!count}>
      <div className="text-ellipsis">
        <Tag
          text={`${t(data[0])}${count}`}
          mediumSelectView
          noBackground
          color={color.black}
        />
      </div>
    </CustomTooltip>
  );

  const handleRedirectOnClick = () => {
    const haveProposal = !!publicRfp.Proposals?.length;
    const requestId = haveProposal ? publicRfp.Proposals[0].id : null;
    const path = haveProposal ? toRequestToUsId(requestId) : toPublicRftId(publicRfp.id);
    navigate(path, {state: haveProposal ? {contractId: null} : null});
  };

  return (
    <div
      className={cn("requests-card_body", {"is-read": isExternal || readedRequest})}
      onClick={() =>
        isExternal
          ? showInfoAndClick &&
            navigate(toExternalRftId(publicRfp.id), {state: {externalRft: publicRfp}})
          : showInfoAndClick && !isHideRfpInfo && handleRedirectOnClick()
      }
    >
      <div className={cn(`${columnClass} __category`, {__ext: isExternal})}>
        {!!categories?.length ? (
          <TooltipTag data={categories} count={countCategories} />
        ) : (
          <Tag
            text={t("PUBLIC_RFPS_PAGE_TABLE_NO_CATEGORY")}
            mediumSelectView
            color={color.midGray}
            noBackground
          />
        )}
      </div>
      <div className={cn(`${columnClass} __region`, {__ext: isExternal})}>
        {!!availableIn?.length ? (
          <TooltipTag data={availableIn} count={countAvailableIn} />
        ) : (
          <Tag
            text={t("PUBLIC_RFPS_PAGE_TABLE_NO_REGION")}
            mediumSelectView
            color={color.midGray}
            noBackground
          />
        )}
      </div>
      <div className={cn(`${columnClass} __published-at`, {__ext: isExternal})}>
        {publicRfp.publishedAt ? dateFormatUTC(publicRfp.publishedAt) : "-"}
      </div>
      <div className={cn(`${columnClass} __time-left`, {__ext: isExternal})}>
        {publicRfp.closedAt ? (
          <CustomTooltip
            text={dateTimeFormatUTC(dateEndDay(publicRfp.closedAt, true))}
            placement="bottom"
            small
          >
            <TimeLeft
              date={dateEndDay(publicRfp.closedAt)}
              language={language}
              hasShortLabels
            />
          </CustomTooltip>
        ) : (
          "-"
        )}
      </div>
      <div className={cn(`${columnClass} __customers`, {__ext: isExternal})}>
        <LockValue
          canPermissions={["READ_OTHER_OPEN_RFT"]}
          customLockIcon={iconDotFilled}
          additionalHideCondition={isHideRfpInfo}
        >
          {publicRfp.Buyer?.name ?? publicRfp.buyerName ?? "-"}
        </LockValue>
      </div>
      {!isExternal && (
        <div className={cn(`${columnClass} __project-name`, {__ext: isExternal})}>
          <LockValue
            canPermissions={["READ_OTHER_OPEN_RFT"]}
            customLockIcon={iconDotFilled}
            additionalHideCondition={isHideRfpInfo}
          >
            {publicRfp.ProjectItem?.Project?.name}
          </LockValue>
        </div>
      )}
      <div className={cn(`${columnClass} __rfp-name`, {__ext: isExternal})}>
        <LockValue
          canPermissions={["READ_OTHER_OPEN_RFT"]}
          customLockIcon={iconDotFilled}
          additionalHideCondition={isHideRfpInfo}
        >
          <ButtonBookmarkByMembers
            bookmarks={publicRfp?.RequestBookmarkedByMembers}
            currentUser={currentUser}
            tooltipEmpty="PUBLIC_RFT_PAGE_BOOKMARK_TOOLTIP"
            tooltipFilled="PUBLIC_RFT_PAGE_BOOKMARK_FILLED_TOOLTIP"
            showStatsOnly
          />
          <CustomTooltip text={isExternal ? publicRfp.name : null} placement="left">
            {publicRfp.name?.slice(0, 100) ?? "-"}
          </CustomTooltip>
        </LockValue>
      </div>
      {!isExternal && showInfoAndClick && (
        <div
          className={cn(`${columnClass} __turnover`, {
            __ext: isExternal,
            __red: isHideRfpInfo,
          })}
        >
          <IconRefresh addClass="mr-1" />
          {`${publicRfp.minRevenue}M`}
        </div>
      )}
    </div>
  );
};

export default PublicRfpsTableItem;
